"use client";

import { ReactNode, useContext, useEffect, useState } from "react";
import { ResourceContext } from "@/contexts/resource-context";
import { AssessmentScoreContext } from "@/contexts/assessment-score-context";
import { logEvent } from "@/service/logs/log";
import { scrollToElementAdaptive } from "@/service/scroll/scroll";
import { RoadmapStepHeader } from "./components/roadmap-step-header";
import { BlocksGroup } from "./components/blocks-group";
import { useQuizProgress } from "./hooks/use-quiz-progress";
import { useStepNavigation } from "./hooks/use-step-navigation";
import { useStepInitialization } from "./hooks/use-step-initialization";
import { useAdvanceControl } from "./hooks/use-advance-control";
import { useScrollDetection } from "./hooks/use-scroll-detection";
import { clientPost } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { ClientContentBlocks } from "@/components/common/content-blocks/client-content-blocks";
import { StepNavigationButton } from "@/components/roadmap/step/resource/components/step-navigation-button";
import { StepProgressBar } from "@/components/roadmap/step/resource/components/step-progress-bar";
import { cn } from "@/lib/utils";

export const RoadmapStepResource = ({
  step,
  path,
  milestones,
  specialization,
  bannerTop,
  bannerBottom,
  accessToken,
}: {
  step: RoadmapStepVM;
  path: RoadmapPathVM;
  milestones: RoadmapMilestoneVM[];
  specialization: RoadmapVM;
  bannerTop?: ReactNode;
  bannerBottom?: ReactNode;
  accessToken?: string | string[];
}) => {
  const tokenString = Array.isArray(accessToken) ? accessToken[0] : accessToken;
  const tokenQuery = tokenString ? `?access-token=${tokenString}` : "";
  const paths = milestones.flatMap((milestone) => milestone.paths);

  const { title, contentBlocksGroups, sourcesBlocks } = step;
  const { logView } = useContext(ResourceContext);
  const [isPathCompleted, setIsPathCompleted] = useState(
    path.progress?.isCompleted ?? false,
  );

  // Initialize quiz progress state
  const { initialBlocksGroupIndex, score, setScore } = useQuizProgress({
    step,
    contentBlocksGroups,
  });

  // Initialize navigation state
  const { isLoading, handleVisitFinish, setIsLoading } = useStepNavigation({
    step,
    path,
    isPathCompleted,
    outroSeen: path.progress?.outroSeen ?? false,
    tokenQuery,
  });

  // Initialize step on mount
  useStepInitialization(step);

  // Manage current blocks group index
  const [currentBlocksGroupIndex, setCurrentBlocksGroupIndex] = useState(
    initialBlocksGroupIndex,
  );

  // Calculate if this is an incremental view
  const isIncrementalView = contentBlocksGroups.length > 1;

  // Scroll detection: only for non-incremental, non-completed steps
  const shouldEnableScrollDetection =
    !isIncrementalView &&
    contentBlocksGroups.length > 0 &&
    !step.progress?.isCompleted;
  const progressFillId = `step-progress-fill-${step.id}`;
  const { hasScrolledToBottom } = useScrollDetection({
    contentElementId: `step-content-${step.id}`,
    progressFillId,
    isEnabled: shouldEnableScrollDetection,
  });

  // Manage advance control state
  const { canAdvance, setCanAdvance } = useAdvanceControl({
    contentBlocksGroups,
    currentBlocksGroupIndex,
    shouldRequireScroll: shouldEnableScrollDetection,
    hasScrolledToBottom,
  });

  const handleNext = () => {
    logEvent("roadmap_step_next", logView, {
      step_id: step.id,
      step_title: title,
      step_slug: step.slug,
      path_id: path.id,
      path_slug: path.slug,
    });

    setCurrentBlocksGroupIndex((prev) => {
      setTimeout(() => {
        scrollToElementAdaptive({
          selector: `#blocks-group-${prev + 1}`,
          offset: 0,
          behavior: "smooth",
        });
      }, 100);
      return prev + 1;
    });
  };

  const handleFinish = async () => {
    setIsLoading(true);
    await handleVisitFinish();
    setIsLoading(false);
  };

  const handleChecklistDownload = () => {
    logEvent("roadmap_step_checklist_download", logView, {
      step_id: step.id,
      step_title: title,
      step_slug: step.slug,
      path_id: path.id,
      path_slug: path.slug,
    });
    window.print();
  };

  const isLastGroup =
    contentBlocksGroups.length === 0 ||
    currentBlocksGroupIndex === contentBlocksGroups.length - 1;

  const isLastStep = path.steps[path.steps.length - 1].id === step.id;
  const currentStepIndex = path.steps.findIndex((s) => s.id === step.id);
  const previousStep =
    currentStepIndex > 0 ? path.steps[currentStepIndex - 1] : null;

  // Check if there are any incomplete steps other than the current one
  const hasOtherIncompleteSteps = path.steps.some(
    (s) => s.id !== step.id && !s.progress?.isCompleted,
  );

  // Find previous path in the same milestone
  const currentMilestoneId = path.milestone.id;
  const pathsInMilestone = paths.filter(
    (p) => p.milestone.id === currentMilestoneId,
  );
  const currentPathIndex = pathsInMilestone.findIndex((p) => p.id === path.id);
  const previousPath =
    currentPathIndex > 0 ? pathsInMilestone[currentPathIndex - 1] : null;
  const previousPathUrl = previousPath
    ? createPath("roadmap.path.show", {
        specialization: specialization.slug,
        path: previousPath.slug,
      }) + tokenQuery
    : null;

  const completeStepOnLastGroupShown = async () => {
    const { isPathCompleted } = await clientPost("roadmap.step.complete", {
      id: step.id,
    });
    setIsPathCompleted(isPathCompleted);
  };

  useEffect(() => {
    if (isLastGroup && !isPathCompleted && isIncrementalView) {
      completeStepOnLastGroupShown();
    }
  }, [isLastGroup]);

  return (
    <>
      <StepProgressBar
        fillId={progressFillId}
        isVisible={shouldEnableScrollDetection}
      />
      {bannerTop}
      <AssessmentScoreContext.Provider value={{ score, setScore }}>
        <div className="@container mx-auto max-w-[var(--content-width)] pb-24 min-[2400px]:max-w-[var(--content-width-1024)] md:pb-16">
          <RoadmapStepHeader
            title={title}
            position={step.position}
            stepId={step.id}
          />

          <div
            className={cn(
              "flex min-h-[75vh] w-full",
              isIncrementalView && "mt-10",
            )}
          >
            <div
              className="step-content-container bg-white"
              id={`step-content-${step.id}`}
            >
              {isIncrementalView ? (
                contentBlocksGroups
                  .slice(0, currentBlocksGroupIndex + 1)
                  .map((blocksGroup, blocksGroupIndex) => (
                    <BlocksGroup
                      key={blocksGroupIndex}
                      blocksGroup={blocksGroup}
                      blocksGroupIndex={blocksGroupIndex}
                      stepId={step.id}
                      setCanAdvance={setCanAdvance}
                      quizzes={step.progress?.quizzes}
                    />
                  ))
              ) : contentBlocksGroups.length > 0 ? (
                <ClientContentBlocks
                  blocks={contentBlocksGroups[currentBlocksGroupIndex].content}
                  theme="roadmap_step"
                  blockOptions={{
                    gradient: step.path.milestone.area.gradient,
                    isChecklist: step.isChecklist,
                  }}
                />
              ) : null}

              {!isLastGroup && isIncrementalView && (
                <div className={"mb-[50vh]"}></div>
              )}

              {sourcesBlocks.length > 0 && canAdvance && isLastGroup && (
                <ClientContentBlocks
                  blocks={sourcesBlocks}
                  theme="roadmap_step"
                />
              )}
            </div>
          </div>
        </div>
      </AssessmentScoreContext.Provider>
      {bannerBottom}
      <StepNavigationButton
        canAdvance={canAdvance}
        isLoading={isLoading}
        isLastGroup={isLastGroup}
        onNext={handleNext}
        onFinish={handleFinish}
        isLastStep={isLastStep}
        outroSeen={path.progress?.outroSeen}
        previousPathUrl={previousPathUrl}
        hasOtherIncompleteSteps={hasOtherIncompleteSteps}
        isChecklist={step.isChecklist}
        onDownload={handleChecklistDownload}
        previousStepUrl={
          previousStep
            ? createPath("roadmap.step.show", {
                specialization: specialization.slug,
                path: previousStep.path.slug,
                step: previousStep.slug ?? "",
              }) + tokenQuery
            : undefined
        }
      />
    </>
  );
};
